'use client'

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'
import { useSession } from '@djotta/auth/client'

interface AssistantQuota {
  remaining: number
  total: number
  resetAt?: number
}

interface AssistantContextType {
  isOpen: boolean
  isMinimized: boolean
  openAssistant: () => void
  closeAssistant: () => void
  minimizeAssistant: () => void
  toggleAssistant: () => void

  // Anonymous & Quota specific
  isAnonymous: boolean
  isAuthenticated: boolean
  isPending: boolean
  isLocked: boolean
  quota: AssistantQuota | null
  refreshQuota: () => Promise<void>
  unlockWithCode: (code: string) => Promise<{ success: boolean; message?: string }>
  isQuotaLockForced: boolean
  setIsQuotaLockForced: (forced: boolean) => void
  lockDismissed: boolean
  setLockDismissed: (dismissed: boolean) => void

  // Navigation & State
  view: 'chat' | 'history'
  setView: (view: 'chat' | 'history') => void
  pendingMessage: string | null
  setPendingMessage: (msg: string | null) => void
  setLoadHandler: (handler: (messages: any[]) => void) => void
  loadChat: (messages: any[]) => void
  loadChatById: (id: string) => Promise<void>
  isLoadingChat: boolean
}

const AssistantContext = createContext<AssistantContextType | undefined>(undefined)

export function AssistantProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession()
  const isAnonymous = useMemo(() => !isPending && !session, [isPending, session])
  const isAuthenticated = useMemo(() => !isPending && !!session, [isPending, session])

  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [view, setView] = useState<'chat' | 'history'>('chat')
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)
  const [loadHandler, setLoadHandler] = useState<((messages: any[]) => void) | undefined>()
  const [isLoadingChat, setIsLoadingChat] = useState(false)
  const [lockDismissed, setLockDismissed] = useState(false)

  const [quota, setQuota] = useState<AssistantQuota | null>(null)
  const [isQuotaLockForced, setIsQuotaLockForced] = useState(false)

  const isLocked = useMemo(() => isAnonymous && quota !== null && quota.remaining <= 0, [isAnonymous, quota])

  const refreshQuota = useCallback(async () => {
    if (!isAnonymous) return
    try {
      const res = await fetch('/api/v1/chat/anonymus/quota')
      if (res.ok) {
        const data = await res.json()
        setQuota({
          remaining: data.remaining,
          total: data.total,
          resetAt: data.resetAt
        })
      }
    } catch (err) {
      console.error('Failed to refresh quota:', err)
    }
  }, [isAnonymous])

  useEffect(() => {
    if (isOpen && isAnonymous) {
      refreshQuota()
    }
  }, [isAnonymous, isOpen, refreshQuota])

  const openAssistant = useCallback(() => {
    setIsOpen(true)
    setIsMinimized(false)
    setView('chat')
  }, [])

  const closeAssistant = useCallback(() => {
    setIsOpen(false)
    setIsMinimized(false)
  }, [])

  const minimizeAssistant = useCallback(() => {
    setIsOpen(false)
    setIsMinimized(true)
  }, [])

  const toggleAssistant = useCallback(() => {
    if (isOpen) {
      minimizeAssistant()
    } else {
      openAssistant()
    }
  }, [isOpen, minimizeAssistant, openAssistant])

  const loadChat = useCallback((messages: any[]) => {
    if (loadHandler) {
      loadHandler(messages)
      setView('chat')
    }
  }, [loadHandler])

  const loadChatById = useCallback(async (id: string) => {
    setIsLoadingChat(true)
    try {
      const res = await fetch(`/api/v1/chat/anonymus/history/${id}`)
      if (res.ok) {
        const data = await res.json()
        if (data.messages && loadHandler) {
          loadHandler(data.messages)
          setView('chat')
        }
      }
    } catch (err) {
      console.error('Failed to load chat by ID:', err)
    } finally {
      setIsLoadingChat(false)
    }
  }, [isAnonymous, loadHandler])

  const unlockWithCode = useCallback(async (code: string) => {
    try {
      const res = await fetch('/api/v1/chat/anonymus/quota', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      })
      const data = await res.json()
      if (data.success) {
        await refreshQuota()
        return { success: true }
      }
      return { success: false, message: data.message }
    } catch (err) {
      return { success: false }
    }
  }, [refreshQuota])

  const value = useMemo(() => ({
    isOpen,
    isMinimized,
    openAssistant,
    closeAssistant,
    minimizeAssistant,
    toggleAssistant,
    isAnonymous,
    isAuthenticated,
    isPending,
    isLocked,
    quota,
    refreshQuota,
    unlockWithCode,
    view,
    setView,
    pendingMessage,
    setPendingMessage,
    setLoadHandler,
    loadChat,
    loadChatById,
    isLoadingChat,
    isQuotaLockForced,
    setIsQuotaLockForced,
    lockDismissed,
    setLockDismissed,
  }), [
    isOpen,
    isMinimized,
    openAssistant,
    closeAssistant,
    minimizeAssistant,
    toggleAssistant,
    isAnonymous,
    isAuthenticated,
    isPending,
    isLocked,
    quota,
    refreshQuota,
    unlockWithCode,
    view,
    setView,
    pendingMessage,
    setPendingMessage,
    setLoadHandler,
    loadChat,
    loadChatById,
    isLoadingChat,
    isQuotaLockForced,
    setIsQuotaLockForced,
    lockDismissed,
    setLockDismissed,
  ])

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  )
}

export function useAssistant() {
  const context = useContext(AssistantContext)
  if (context === undefined) {
    throw new Error('useAssistant must be used within an AssistantProvider')
  }
  return context
}
